// src/components/PayoutCalculatorModal.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function PayoutCalculatorModal({
    onClose,
    onSave,
    initialPayoutConfig = {}
}) {
    const [buyIn, setBuyIn] = useState(initialPayoutConfig.buyIn || 0);
    const [greenFees, setGreenFees] = useState(initialPayoutConfig.greenFees || 0);
    const [barAddition, setBarAddition] = useState(initialPayoutConfig.barAddition || 0);
    const [numberOfPlaces, setNumberOfPlaces] = useState(initialPayoutConfig.numberOfPlaces || 3);
    const [percentages, setPercentages] = useState(
        initialPayoutConfig.percentages || [50, 30, 20]
    );
    const [playerCount, setPlayerCount] = useState(initialPayoutConfig.playerCount || 8);

    // Check if mobile (same breakpoint as used elsewhere in app)
    const isMobile = window.innerWidth < 1000;

    // Ensure percentages array matches numberOfPlaces
    useEffect(() => {
        if (percentages.length !== numberOfPlaces) {
            const newPercentages = [...percentages];

            // Add default percentages if array is too short
            while (newPercentages.length < numberOfPlaces) {
                newPercentages.push(10);
            }

            // Remove excess percentages if array is too long
            while (newPercentages.length > numberOfPlaces) {
                newPercentages.pop();
            }

            setPercentages(newPercentages);
        }
    }, [numberOfPlaces, percentages]);

    // Calculate total pot
    const totalPot = (buyIn * playerCount) - (greenFees * playerCount) + barAddition;

    // Calculate individual payouts
    const calculatePayouts = () => {
        return percentages.map(percentage => {
            const exactAmount = (totalPot * percentage) / 100;
            // Round to nearest $5
            return Math.round(exactAmount / 5) * 5;
        });
    };

    const payouts = calculatePayouts();
    const totalPayout = payouts.reduce((sum, amount) => sum + amount, 0);
    const totalPercentage = percentages.reduce((sum, pct) => sum + pct, 0);

    const handlePercentageChange = (index, value) => {
        const newPercentages = [...percentages];
        newPercentages[index] = Number(value) || 0;
        setPercentages(newPercentages);
    };

    const handleSave = () => {
        const payoutConfig = {
            buyIn: Number(buyIn),
            greenFees: Number(greenFees),
            barAddition: Number(barAddition),
            numberOfPlaces: Number(numberOfPlaces),
            percentages: percentages,
            playerCount: Number(playerCount),
            payouts: payouts,
            totalPot: totalPot
        };
        onSave(payoutConfig);
        onClose();
    };

    const getPlaceSuffix = (place) => {
        if (place === 1) return 'st';
        if (place === 2) return 'nd';
        if (place === 3) return 'rd';
        return 'th';
    };

    const modalContent = (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'var(--bg-modal-overlay)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}
        >
            <div
                style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: isMobile ? 6 : 8,
                    maxWidth: isMobile ? '95%' : 500,
                    width: isMobile ? '95%' : '90%',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: 'var(--shadow-lg)',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sticky Header */}
                <div style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'var(--bg-secondary)',
                    padding: isMobile ? 16 : 24,
                    paddingBottom: isMobile ? 12 : 16,
                    borderBottom: '1px solid var(--border-color)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <h3 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 'bold' }}>
                        Payout Calculator
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: 24,
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            padding: 0,
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'var(--btn-secondary-hover)';
                            e.target.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = 'var(--text-secondary)';
                        }}
                        title="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable Content */}
                <div style={{
                    padding: isMobile ? 16 : 24,
                    paddingTop: isMobile ? 12 : 16,
                    overflowY: 'auto',
                    flex: 1
                }}>

                {/* Basic Settings */}
                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>
                        Number of Players
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={playerCount}
                        onChange={(e) => setPlayerCount(Number(e.target.value) || 0)}
                        style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '8px',
                            fontSize: isMobile ? 16 : 14,
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            boxSizing: 'border-box'
                        }}
                    />
                </div>

                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>
                        Buy-In Amount ($)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="5"
                        value={buyIn}
                        onChange={(e) => setBuyIn(Number(e.target.value) || 0)}
                        style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '8px',
                            fontSize: isMobile ? 16 : 14,
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            boxSizing: 'border-box'
                        }}
                    />
                </div>

                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>
                        Green Fees ($)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="5"
                        value={greenFees}
                        onChange={(e) => setGreenFees(Number(e.target.value) || 0)}
                        style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '8px',
                            fontSize: isMobile ? 16 : 14,
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            boxSizing: 'border-box'
                        }}
                    />
                    <div style={{ fontSize: isMobile ? 11 : 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Fees deducted from total pot
                    </div>
                </div>

                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>
                        Bar Addition ($)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="5"
                        value={barAddition}
                        onChange={(e) => setBarAddition(Number(e.target.value) || 0)}
                        style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '8px',
                            fontSize: isMobile ? 16 : 14,
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            boxSizing: 'border-box'
                        }}
                    />
                    <div style={{ fontSize: isMobile ? 11 : 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Additional amount added to pot
                    </div>
                </div>

                {/* Total Pot Display */}
                <div style={{
                    padding: isMobile ? 10 : 12,
                    backgroundColor: 'var(--info-bg)',
                    borderRadius: 6,
                    marginBottom: isMobile ? 16 : 20,
                    border: '2px solid var(--info)'
                }}>
                    <div style={{ fontSize: isMobile ? 12 : 14, color: 'var(--text-secondary)', marginBottom: 4 }}>Total Pot</div>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 'bold', color: 'var(--info)' }}>
                        ${totalPot.toFixed(2)}
                    </div>
                    <div style={{ fontSize: isMobile ? 11 : 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        ({playerCount} × ${buyIn}) - ({playerCount} × ${greenFees}) + ${barAddition}
                    </div>
                </div>

                {/* Number of Places */}
                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>
                        Number of Places Paid
                    </label>
                    <input
                        type="number"
                        min="1"
                        max="10"
                        value={numberOfPlaces}
                        onChange={(e) => setNumberOfPlaces(Number(e.target.value) || 1)}
                        style={{
                            width: '100%',
                            padding: isMobile ? '10px' : '8px',
                            fontSize: isMobile ? 16 : 14,
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            boxSizing: 'border-box'
                        }}
                    />
                </div>

                {/* Percentage Distribution */}
                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>
                        Payout Distribution
                    </label>

                    {percentages.map((percentage, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: isMobile ? 10 : 8,
                            gap: isMobile ? 6 : 8
                        }}>
                            <div style={{ minWidth: isMobile ? 50 : 60, fontSize: isMobile ? 12 : 14, fontWeight: '500' }}>
                                {index + 1}{getPlaceSuffix(index + 1)}{isMobile ? '' : ' Place'}:
                            </div>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={percentage}
                                onChange={(e) => handlePercentageChange(index, e.target.value)}
                                style={{
                                    width: isMobile ? 60 : 80,
                                    padding: isMobile ? '8px' : '6px 8px',
                                    fontSize: isMobile ? 16 : 14,
                                    border: '1px solid var(--border-color)',
                                    borderRadius: 4,
                                    textAlign: 'center'
                                }}
                            />
                            <span style={{ fontSize: isMobile ? 12 : 14 }}>%</span>
                            <div style={{
                                flex: 1,
                                textAlign: 'right',
                                fontSize: isMobile ? 14 : 16,
                                fontWeight: 'bold',
                                color: 'var(--success)'
                            }}>
                                ${payouts[index]?.toFixed(2) || '0.00'}
                            </div>
                        </div>
                    ))}

                    {/* Total Percentage Check */}
                    <div style={{
                        marginTop: isMobile ? 10 : 12,
                        padding: isMobile ? 10 : 8,
                        backgroundColor: totalPercentage === 100 ? 'var(--success-bg)' : 'var(--warning-bg)',
                        borderRadius: 4,
                        border: `1px solid ${totalPercentage === 100 ? 'var(--success)' : 'var(--warning)'}`
                    }}>
                        <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 'bold' }}>
                            Total: {totalPercentage}%
                            {totalPercentage !== 100 && (
                                <span style={{ color: '#856404', marginLeft: isMobile ? 4 : 8, display: isMobile ? 'block' : 'inline', marginTop: isMobile ? 4 : 0 }}>
                                    (Should equal 100%)
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 'bold', marginTop: 4 }}>
                            Total Payout: ${totalPayout.toFixed(2)}
                        </div>
                    </div>
                </div>
                </div>

                {/* Action Buttons - Sticky Footer */}
                <div style={{
                    padding: isMobile ? 16 : 24,
                    paddingTop: isMobile ? 12 : 16,
                    borderTop: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-secondary)',
                    display: 'flex',
                    gap: isMobile ? 6 : 8,
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: isMobile ? '10px 14px' : '8px 16px',
                            fontSize: isMobile ? 15 : 14,
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            backgroundColor: 'var(--bg-secondary)',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={totalPercentage !== 100}
                        style={{
                            padding: isMobile ? '10px 14px' : '8px 16px',
                            fontSize: isMobile ? 15 : 14,
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: totalPercentage === 100 ? 'var(--success)' : 'var(--border-color)',
                            color: 'var(--text-on-dark)',
                            cursor: totalPercentage === 100 ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold'
                        }}
                    >
                        Save Payouts
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}
